// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a React hook for fetching location data.
 * Sorting and filtering are performed server-side.
 */
import { LocationFilter, getLocations } from '@/location/api/get-locations';
import { ONE_MINUTE_MS, TWO_MINUTES_MS } from '@/utilities/time-constants';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Schema defining valid properties for sorting locations server-side.
 * Address sort is not supported — it requires structured address comparison logic.
 */
export const locationSortPropertySchema = z.enum([
  'name',
  'company',
  'assetCount',
]);

export type LocationSortProperty = z.infer<typeof locationSortPropertySchema>;

/**
 * Configuration options for the useLocations hook.
 */
interface UseLocationsOptions {
  filter?: LocationFilter;
}

/**
 * A React hook that requests a collection of locations from the remote API.
 * This hook suspends while requesting the locations.
 *
 * Sort and filter are sent as server-side parameters via the filter object.
 * Callers that omit page/pageSize receive all locations.
 *
 * @param options - Configuration for filtering locations
 * @returns Object containing locations array, total result count, and query metadata
 */
export function useLocations(options: UseLocationsOptions = {}) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: ['location', options.filter],
    queryFn: () => getLocations(options.filter),
    refetchOnWindowFocus: true,
    staleTime: ONE_MINUTE_MS,
    refetchInterval: TWO_MINUTES_MS,
  });

  return { locations: data.locations, resultCount: data.totalCount, ...query };
}
